import React, { useState, useEffect, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaGripLines, FaArrowLeft, FaArrowRight, FaChevronRight } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { RiImageAddLine } from "react-icons/ri";
import Pagination from "../../../../../libs/Pagination";
import { addCategory, categorySequence, deleteCategory, getCategories, getCategoriesDropdown, updateCategory, swapCategoryWithPage } from "../../../../../libs/api/categoryApi";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../../libs/LoadingSpinner";
import Swal from "sweetalert2";
import { AppContext } from "../../../../../context/appContext";

export default function Category({ currentCategoryId = null, onNavigateToCategory = null, breadcrumb = [] }) {
  const { t, formatNum, isRTL } = useContext(AppContext);
  const [categories, setCategories] = useState([]);
  const [allCategoriesForDropdown, setAllCategoriesForDropdown] = useState([]);
  const [categoryImage, setCategoryImage] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [parentId, setParentId] = useState("");
  const [isHidden, setIsHidden] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [errors, setErrors] = useState({ title: "", image: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loadingAddForm, setLoadingAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  useEffect(() => {
    fetchCategories(true);
  }, [currentPage, limit, debouncedSearch, currentCategoryId]);

  useEffect(() => {
    fetchAllCategoriesForDropdown();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchCategories = async (isLoading) => {
    setLoading(isLoading);
    try {
      const parentIdFilter = currentCategoryId === null ? 'null' : currentCategoryId;
      const response = await getCategories(currentPage, limit, debouncedSearch, 'false', parentIdFilter);
      if (response.status === 200) {
        const categoriesWithSequence = response.data.data.map((category, index) => ({
          ...category,
          sequence: category.sequence || index + 1
        }));
        setCategories(categoriesWithSequence || []);
        setTotalPages(response.data.totalPages || 1);
        setLimit(response.data.limit || 10);
        setCurrentPage(response.data.currentPage || 1);
        setTotalRecords(response.data.totalRecords || 0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  }

  const fetchAllCategoriesForDropdown = async () => {
    try {
      const response = await getCategoriesDropdown();
      if (response.status === 200) {
        setAllCategoriesForDropdown(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories for dropdown:', error);
    }
  }

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'title':
        if (!value || !value.toString().trim()) {
          error = t('validation.categoryTitleRequired');
        }
        break;
      case 'image':
        if (modalMode === "add" && !value) {
          error = t('validation.categoryImageRequired');
        } else if (value && value.size && value.size > 25 * 1024 * 1024) {
          error = t('validation.imageSizeTooLarge');
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleFieldChange = (name, value) => {
    if (name === 'title') setTitle(value);
    if (name === 'description') setDescription(value);
    if (name === 'parentId') setParentId(value);
    if (name === 'image') setCategoryImage(value);

    const error = validateField(name, value);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    newErrors.title = validateField('title', title);
    newErrors.image = validateField('image', categoryImage);
    setErrors(newErrors);

    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    const formData = new FormData();
    formData.append('title', title);
    if (description) formData.append('description', description);
    if (parentId) formData.append('parentId', parentId);
    formData.append('isHidden', isHidden ? 'true' : 'false');
    if (categoryImage) {
      formData.append('image', categoryImage);
    }

    try {
      setLoadingAddForm(true);
      const response = await addCategory(formData);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("category_modal").close();
        toast.success(response?.data?.message || t('messages.categoryAddedSuccess'));
        fetchCategories(false);
        fetchAllCategoriesForDropdown();
        clearForm();
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  };

  const handleUpdate = async () => {
    const newErrors = {};
    newErrors.title = validateField('title', title);
    setErrors(newErrors);

    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    const formData = new FormData();
    formData.append('title', title);
    if (description) formData.append('description', description);
    if (parentId) {
      formData.append('parentId', parentId);
    } else {
      formData.append('parentId', '');
    }
    formData.append('isHidden', isHidden ? 'true' : 'false');
    if (categoryImage && typeof categoryImage !== 'string') {
      formData.append('image', categoryImage);
    }

    try {
      setLoadingAddForm(true);
      const response = await updateCategory(selectedCategoryId, formData);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("category_modal").close();
        toast.success(response?.data?.message || t('messages.categoryUpdatedSuccess'));
        fetchCategories(false);
        fetchAllCategoriesForDropdown();
        clearForm();
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  }

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: t('messages.areYouSure'),
      text: t('messages.categoryDeleteWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yesDelete'),
      cancelButtonText: t('common.cancel')
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await deleteCategory(id);
      if (response.status === 200 || response.status === 201) {
        toast.success(response?.data?.message || t('messages.categoryDeletedSuccess'));
        fetchCategories(false);
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  }

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const openModal = (mode, category = null) => {
    setModalMode(mode);
    setSelectedCategoryId(null);
    if (mode === "edit" && category) {
      setTitle(category?.title || "");
      setDescription(category?.description || "");
      setParentId(category?.parentId || "");
      setIsHidden(category?.isHidden || false);
      setCategoryImage(category?.image || null);
      setSelectedCategoryId(category?._id || null);
      setErrors({ title: "", image: "" });
    } else {
      setTitle("");
      setDescription("");
      setParentId("");
      setIsHidden(false);
      setCategoryImage(null);
      setSelectedCategoryId(null);
      setErrors({ title: "", image: "" });
    }

    document.getElementById("category_modal").showModal();
  };

  const clearForm = () => {
    setTitle("");
    setDescription("");
    setParentId("");
    setIsHidden(false);
    setCategoryImage(null);
    setSelectedCategoryId(null);
    setErrors({ title: "", image: "" });
  }

  const handleDragStart = (e, index) => {
    setDraggedItem(categories[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);

    dragImage.style.width = rect.width + 'px';
    dragImage.style.height = rect.height + 'px';
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    dragImage.style.backgroundColor = 'white';
    dragImage.style.border = '2px solid #0D6EFD';
    dragImage.style.borderRadius = '8px';
    dragImage.style.zIndex = '9999';

    draggedElement.style.opacity = '0.3';

    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(categories[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = categories[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedCategories = categories.map(category => {
      if (category._id === draggedItem._id) {
        return { ...category, sequence: targetSequence };
      }
      if (category._id === draggedOverItem._id) {
        return { ...category, sequence: draggedSequence };
      }
      return category;
    });

    // Sort by sequence to reflect the swap
    updatedCategories.sort((a, b) => a.sequence - b.sequence);

    setCategories(updatedCategories);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);

    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedToUpdateSequence'));
      fetchCategories(false);
    }
  };

  const handleDragEnd = (e) => {
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence
      }
      const response = await categorySequence(payload);
      if (response.status === 200 || response.status === 201) {
        fetchCategories(false);
      }
    } catch (error) {
      toast.error(error?.data?.message || t('messages.somethingWentWrong'));
    }
  }

  const handleSwapWithPage = async (categoryId, direction) => {
    try {
      const payload = {
        id: categoryId,
        direction: direction,
        currentPage: currentPage,
        limit: limit
      };
      const response = await swapCategoryWithPage(payload);
      if (response.status === 200) {
        toast.success(response.data.message || t('messages.categoryMovedSuccessfully'));
        fetchCategories(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  return (
    <div>
      <div className="bg-white rounded-3xl shadow">
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('forms.searchPlaceholder')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2 border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('category.addCategory')}
          </button>
        </div>

        {loading && <div className="flex justify-center items-center h-full p-6"><LoadingSpinner /></div>}
        {!loading && categories.length === 0 && (
          <div className="flex justify-center items-center h-full p-6">
            <p className="text-gray-500">{t('category.noCategories')}</p>
          </div>
        )}
        {!loading && categories.length > 0 && (
          <div className="w-full">
            <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-7 border-b border-[#F7F7F9]">
              <div className={`p-2 py-4 ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('common.id')}</div>
              <div className="p-2 py-4">{t('category.image')}</div>
              <div className="p-2 py-4">{t('category.categoryTitle')}</div>
              <div className="p-2 py-4">{t('category.parent')}</div>
              <div className="p-2 py-4">{t('category.description')}</div>
              <div className="p-2 py-4">{t('category.contentCount')}</div>
              <div className="p-2 py-4">{t('common.actions')}</div>
            </div>

            <div>
              {categories.map((category, index) => (
                <div
                  key={category._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`grid grid-cols-7 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none ${
                    draggedItem?._id === category._id ? "opacity-50 bg-gray-100 shadow-lg" : ""
                  } ${
                    dragOverItem?._id === category._id ? "border-t-2 border-blue-500 bg-blue-50" : ""
                  }`}
                  style={{
                    cursor: 'move',
                    transition: 'all 0.3s ease',
                    userSelect: 'none'
                  }}
                >
                  <div className={`p-2 py-6 ${isRTL ? 'pr-8' : 'pl-8'} flex items-center gap-2`}>
                    <FaGripLines className="text-gray-400" />
                    {formatNum((currentPage - 1) * limit + index + 1)}
                  </div>
                  <div className="p-2 py-6">
                    {category?.image ? (
                      <img src={category.image} alt={category.title} className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-400">
                        {t('common.na')}
                      </div>
                    )}
                  </div>
                  <div className="p-2 py-6 flex items-center gap-2">
                    <span>{category?.title || "-"}</span>
                    {category?.isHidden && (
                      <span className="badge badge-sm bg-gray-200 text-gray-600 border-0">{t('forms.hidden')}</span>
                    )}
                  </div>
                  <div className="p-2 py-6">
                    {category?.parentId ? (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        {breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 1].title : t('category.parent')}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">{t('category.topLevel')}</span>
                    )}
                  </div>
                  <div className="p-2 py-6">{category?.description || "-"}</div>
                  <div className="p-2 py-6">{formatNum(category?.contentCount || 0)}</div>
                  <div className="p-2 py-6">
                    <div className="flex gap-3 items-center">
                      {index === 0 && currentPage > 1 && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(category._id, 'previous');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToPreviousPage')}
                        >
                          <FaArrowLeft size={18} />
                        </button>
                      )}
                      {index === limit - 1 && currentPage < totalPages && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(category._id, 'next');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToNextPage')}
                        >
                          <FaArrowRight size={18} />
                        </button>
                      )}
                      {onNavigateToCategory && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigateToCategory(category._id, category.title);
                          }}
                          className="text-green-600 hover:text-green-800 transition-colors cursor-pointer"
                          title={t('category.viewChildren') || 'View Children'}
                        >
                          <FaChevronRight size={18} />
                        </button>
                      )}
                      <BiEditAlt
                        size={24}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => openModal("edit", category)}
                      />
                      <FaRegTrashCan
                        size={20}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => handleDelete(category._id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && categories.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((currentPage - 1) * limit + 1)} {t('pagination.to')} {formatNum(Math.min(currentPage * limit, totalRecords))} {t('pagination.of')} {formatNum(totalRecords)} {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <dialog
        id="category_modal"
        className="modal shadow-[0px_1px_2px_0px_#0A0D120F]"
      >
        <div className="modal-box min-w-[800px] rounded-2xl p-0">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {modalMode === "add" ? t('category.addCategory') : t('category.editCategory')}
              </h3>
              <form method="dialog">
                <button className="text-xl hover:text-red-500">
                  <IoClose />
                </button>
              </form>
            </div>

            {/* Title */}
            <label className="block text-sm font-medium mb-2">{t('category.categoryTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder={t('category.enterCategoryTitle')}
              className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.title && <p className="text-red-500 absolute -mt-[15px] text-xs">{errors.title}</p>}

            {/* Description */}
            <label className="block text-sm font-medium mb-2 mt-4">{t('category.descriptionOptional')}</label>
            <textarea
              value={description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder={t('category.enterCategoryDescription')}
              className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
              rows="3"
            />

            {/* Parent Category */}
            <label className="block text-sm font-medium mb-2 mt-4">{t('category.parentCategoryOptional')}</label>
            <select
              value={parentId}
              onChange={(e) => handleFieldChange('parentId', e.target.value)}
              className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t('category.noneTopLevel')}</option>
              {allCategoriesForDropdown
                .filter(cat => cat._id !== selectedCategoryId)
                .map(cat => (
                  <option key={cat._id} value={cat._id}>
                    {cat.displayTitle || cat.title}
                  </option>
                ))}
            </select>

            {/* Category Image */}
            <div className="my-5 mt-1">
              <label className="block text-sm font-medium mb-2">
                {t('category.categoryImage')} {modalMode === "add" && t('category.required')}
              </label>
              <div
                className="relative w-full border-dashed border-2 border-gray-300 p-6 text-center rounded-xl cursor-pointer hover:bg-gray-50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file && file.type.startsWith("image/")) {
                    if (file.size <= 25 * 1024 * 1024) {
                      setCategoryImage(file);
                      handleFieldChange('image', file);
                      setErrors((prevErrors) => ({ ...prevErrors, image: '' }));
                    } else {
                      setErrors((prevErrors) => ({ ...prevErrors, image: t('validation.imageSizeTooLarge') }));
                    }
                  }
                }}
                onClick={() =>
                  document.getElementById("categoryImageUploadInput").click()
                }
              >
                {categoryImage ? (
                  <img
                    src={typeof categoryImage === 'string' ? categoryImage : URL.createObjectURL(categoryImage)}
                    alt="category"
                    className="max-h-40 mx-auto mb-2"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
                    <div className="bg-gray-100 rounded-full min-h-12 min-w-12 flex items-center justify-center">
                      <RiImageAddLine size={25} className="text-blue-500" />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-md text-blue-500 font-medium">
                        {t('category.clickToUpload')}
                      </p>
                      <p className="text-md text-black">{t('category.orDragAndDrop')}</p>
                    </div>
                    <p className="text-sm text-black">
                      {t('category.maxFileSize')}
                    </p>
                  </div>
                )}
                <input
                  id="categoryImageUploadInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size <= 25 * 1024 * 1024) {
                        setCategoryImage(file);
                        handleFieldChange('image', file);
                        setErrors((prevErrors) => ({ ...prevErrors, image: '' }));
                      } else {
                        setErrors((prevErrors) => ({ ...prevErrors, image: t('validation.imageSizeTooLarge') }));
                      }
                    }
                    e.target.value = '';
                  }}
                />
              </div>
            </div>
            {errors.image && <p className="text-red-500 absolute -mt-[18px] text-xs">{errors.image}</p>}

            {/* Hidden */}
            <div className="flex items-center gap-2 mt-8">
              <input
                type="checkbox"
                id="category_isHidden"
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                className="checkbox checkbox-sm"
              />
              <label htmlFor="category_isHidden" className="text-sm font-medium cursor-pointer">
                {t('forms.hidden')}
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="modal-action bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
            <button
              type="button"
              disabled={loadingAddForm || loading}
              onClick={modalMode === "add" ? handleSubmit : handleUpdate}
              className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
            >
              {loadingAddForm ? <LoadingSpinner /> : modalMode === "add" ? t('common.add') : t('common.update')}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
